import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getPresignedUrl, downloadFile } from '../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import FileViewerDrawer from '../DocumentViewer/FileViewerDrawer';

interface MagicSignPreviewerProps {
    docIdToView: string;
    setDocIdToView: React.Dispatch<React.SetStateAction<string>>;
    docName: string;
}

/**
 * Magic Sign's document preview drawer — resolves the presigned URL for
 * `docIdToView` and renders it through the shared FileViewerDrawer (the same
 * drawer Data Room and Magic Upload use for pdf/image previews).
 *
 * Replaces the old MagicUploadFilesTable/MagicUploadPreviewer (a bespoke iframe
 * component), which was removed when Data Room/Magic Upload migrated to
 * FileViewerDrawer for Add-to-Reports parity — Magic Sign documents are always
 * PDFs (MAGIC_SIGN_ALLOWED_TYPES), so this keeps the same docId-in/docName-in
 * API its callers (MagicSign.tsx, GlobalMagicSign.tsx) already use.
 */
const MagicSignPreviewer: React.FC<MagicSignPreviewerProps> = ({ docIdToView, setDocIdToView, docName }) => {
    const [fileUrl, setFileUrl] = useState('');
    const [mimeType, setMimeType] = useState('');

    useEffect(() => {
        if (!docIdToView) {
            setFileUrl('');
            setMimeType('');
            return;
        }
        let cancelled = false;
        (async () => {
            const res = await getPresignedUrl({ docId: Number(docIdToView) }, false);
            if (cancelled) return;
            if (!res) {
                toast.error('Error in fetching file');
                setDocIdToView('');
                return;
            }
            setFileUrl(res.uri);
            setMimeType(res.fileType ?? 'application/pdf');
        })();
        return () => {
            cancelled = true;
        };
    }, [docIdToView, setDocIdToView]);

    if (!docIdToView || !fileUrl) return null;

    return (
        <FileViewerDrawer
            heading={docName}
            fileUrl={fileUrl}
            mimeType={mimeType}
            onDownload={() => downloadFile(docIdToView, () => {}, docName)}
            onClose={() => setDocIdToView('')}
        />
    );
};

export default MagicSignPreviewer;
